import { victoryCue, type VictoryReason } from './victory-presentation';
import type { Character } from '../game/types';
export type { Character } from '../game/types';
export type IntroResult = 'completed' | 'cancelled' | 'silent';
export const INTRO_LINES: Readonly<Record<Character, string>> = {
  saibaimen: '[Battle cry]',
  goku: "Something tells me I'm not going to want to hold anything back for this one.",
  gohan: "My name is Gohan. Let's have a good fight.",
  krillin: 'No more fooling around.',
  vegeta: "I don't think you realize just who you're dealing with.",
  nappa: "I'm tired of playing around.",
  piccolo: "It's useless. You can't win!",
  raditz: 'Coming here with those pitiful levels, thinking you can take me on!',
};
/** Explicit IDs keep ally voices and alternate personality powers tied to the printed character. */
export const POWER_VOICES: Readonly<Record<string,string>> = {
 ss243:'saibaimen-power',ss244:'saibaimen-power',ss245:'saibaimen-power',ss246:'saibaimen-power',
 ss158:'goku-power',ss159:'goku-kaioken',ss160:'goku-power',ss179:'goku-power',
 ss161:'piccolo-special',ss162:'piccolo-special',ss163:'piccolo-power',ss180:'piccolo-power',
 ss164:'gohan-power',ss165:'gohan-power',ss166:'gohan-power',ss181:'gohan-power',
 ss167:'krillin-power',ss168:'krillin-destructo',ss169:'krillin-power',ss182:'krillin-power',
 ss170:'raditz-power',ss171:'raditz-power',ss172:'raditz-power',ss183:'raditz-power',
 ss173:'vegeta-power',ss174:'vegeta-power',ss175:'vegeta-power',ss184:'vegeta-power',
 ss176:'nappa-power',ss177:'nappa-power',ss178:'nappa-power',ss185:'nappa-power',
};
type CardSound = 'physical' | 'energy' | 'non-combat' | 'drill' | 'ally' | 'dragon-ball' | string;
type SoundEvent = { type: string; cardId?: string; player?: number; message?: string };
type AudioManifest = { assets: { id: string; path: string }[]; voiceSlots?: Record<string, string | null> };

/** Audio is presentation only: never advances the engine or consumes its RNG. */
export class GameAudio {
  private ctx?: AudioContext;
  private master?: GainNode;
  private effects?: GainNode;
  private voices?: GainNode;
  private compressor?: DynamicsCompressorNode;
  private buffers = new Map<string, AudioBuffer>();
  private raw = new Map<string, ArrayBuffer>();
  private loading?: Promise<void>;
  private decoding?: Promise<void>;
  private voiceSlots: Record<string, string | null> = {};
  private voiceSources = new Set<AudioBufferSourceNode>();
  private effectSources: AudioBufferSourceNode[] = [];
  private voiceEnd = 0;
  private voiceGeneration = 0;
  private intro?: {
    resolve: (result: IntroResult) => void;
    onSpeaker?: (character: Character | null) => void;
    timers: Set<ReturnType<typeof setTimeout>>;
  };
  private lastPlayed = new Map<string, number>();
  private _volume = 0.35;
  private _voiceVolume = 0.85;

  get volume() { return this._volume; }
  set volume(value: number) {
    this._volume = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(this._volume, this.ctx.currentTime, 0.025);
    if (!this._volume) this.stopVoices();
  }
  get voiceVolume() { return this._voiceVolume; }
  set voiceVolume(value: number) {
    this._voiceVolume = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
    if (this.voices && this.ctx) this.voices.gain.setTargetAtTime(this._voiceVolume, this.ctx.currentTime, 0.025);
    if (!this._voiceVolume) this.stopVoices();
  }

  /** Fetch local files early; an AudioContext is created only after a gesture. */
  preload(): Promise<void> {
    return this.loading ??= (async () => {
      try {
        const response = await fetch(new URL('/data/audio-manifest.json', location.href).href);
        if (!response.ok) return;
        const manifest = await response.json() as AudioManifest;
        this.voiceSlots = manifest.voiceSlots ?? {};
        const paths = new Map(manifest.assets.map(asset => [asset.id, asset.path]));
        for (const [id, path] of Object.entries(this.voiceSlots)) if (path) paths.set(id, path);
        await Promise.allSettled([...paths].map(async ([id, path]) => {
          const asset = await fetch(new URL(path, location.href).href);
          if (asset.ok) this.raw.set(id, await asset.arrayBuffer());
        }));
        await this.decode();
      } catch { /* Audio cannot prevent playing an offline match. */ }
    })();
  }

  unlock() {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext({ latencyHint: 'interactive' });
        this.master = this.ctx.createGain(); this.master.gain.value = this._volume;
        this.effects = this.ctx.createGain(); this.effects.gain.value = 0.8;
        this.voices = this.ctx.createGain(); this.voices.gain.value = this._voiceVolume;
        this.compressor = this.ctx.createDynamicsCompressor();
        this.compressor.threshold.value = -10; this.compressor.knee.value = 18;
        this.compressor.ratio.value = 4; this.compressor.attack.value = 0.004;
        this.compressor.release.value = 0.16;
        this.effects.connect(this.compressor); this.voices.connect(this.compressor);
        this.compressor.connect(this.master); this.master.connect(this.ctx.destination);
      } catch { return; }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
    void this.preload().then(() => this.decode());
  }

  private decode(): Promise<void> {
    if (!this.ctx || !this.raw.size) return Promise.resolve();
    return this.decoding ??= Promise.allSettled([...this.raw].map(async ([id, data]) => {
      if (!this.buffers.has(id)) this.buffers.set(id, await this.ctx!.decodeAudioData(data.slice(0)));
      this.raw.delete(id);
    })).then(() => { this.decoding = undefined; });
  }

  /** Call only for a new match after unlock(); callers may supply actual first-player order. */
  startMatch(onSpeaker?: (character: Character | null) => void, orderedCharacters: readonly string[] = ['piccolo', 'raditz']): Promise<IntroResult> {
    this.stopVoices();
    const requested = orderedCharacters.filter((character): character is Character => Object.hasOwn(INTRO_LINES, character));
    if (!requested.length || !this.ctx || this.ctx.state !== 'running' || !this._volume || !this._voiceVolume) return Promise.resolve('silent');
    const generation = this.voiceGeneration;
    return new Promise(resolve => {
      this.intro = { resolve, onSpeaker, timers: new Set() };
      // Audio may be suspended or a file may fail: never hold the match indefinitely.
      this.introTimer(() => {
        this.finishIntro('silent');
        this.stopVoices();
      }, 15_000);
      void this.preload().then(() => this.decode()).then(() => {
        if (generation !== this.voiceGeneration || !this.intro) return;
        const characters = requested.filter(character => this.buffers.has(`${character}-intro`));
        if (!this.ctx || this.ctx.state !== 'running' || !this._volume || !this._voiceVolume || !characters.length) {
          this.finishIntro('silent'); return;
        }
        const next = (index: number) => {
          if (generation !== this.voiceGeneration || !this.intro) return;
          if (index === characters.length) { this.finishIntro('completed'); return; }
          if (this.ctx?.state !== 'running') { this.finishIntro('silent'); this.stopVoices(); return; }
          const character = characters[index];
          this.notifySpeaker(character);
          const queued = this.queueVoice(`${character}-intro`, 0, () => {
            if (generation !== this.voiceGeneration || !this.intro) return;
            // The second speaker is triggered by actual audio completion, never a guessed duration.
            if (index + 1 === characters.length) this.finishIntro('completed');
            else { this.notifySpeaker(null); this.introTimer(() => next(index + 1), 250); }
          });
          if (!queued) this.finishIntro('silent');
        };
        this.play('scouter');
        this.introTimer(() => next(0), 350);
      }).catch(() => { if (generation === this.voiceGeneration) this.finishIntro('silent'); });
    });
  }

  private introTimer(callback: () => void, delay: number) {
    const intro = this.intro; if (!intro) return;
    const timer = setTimeout(() => { intro.timers.delete(timer); if (this.intro === intro) callback(); }, delay);
    intro.timers.add(timer);
  }

  private notifySpeaker(character: Character | null) {
    try { this.intro?.onSpeaker?.(character); } catch { /* A caption cannot interrupt audio cleanup. */ }
  }

  private finishIntro(result: IntroResult) {
    const intro = this.intro; if (!intro) return;
    this.notifySpeaker(null);
    this.intro = undefined;
    for (const timer of intro.timers) clearTimeout(timer);
    intro.resolve(result);
  }

  announceResult(winner: Character, loser: Character, reason:VictoryReason='survival') {
    this.stopVoices();
    if(!this.ctx||this.ctx.state!=='running'||!this._volume)return;
    if(reason==='concession')this.play('scouter');else if(reason!=='dragon-balls')this.play('victory');
    const generation = this.voiceGeneration;
    void this.preload().then(() => this.decode()).then(() => {
      if (generation !== this.voiceGeneration||this.ctx?.state!=='running') return;
      const spoke = this.queueVoice(victoryCue(reason,winner), reason==='concession'?.2:.6);
      this.queueVoice(`${loser}-loss`, spoke ? 0.3 : 0.6);
    });
  }

  stopVoices() {
    this.voiceGeneration++;
    this.finishIntro('cancelled');
    for (const source of this.voiceSources) { try { source.stop(); } catch { /* already stopped */ } }
    this.voiceSources.clear(); this.voiceEnd = 0;
    if (this.effects && this.ctx) {
      this.effects.gain.cancelScheduledValues(this.ctx.currentTime);
      this.effects.gain.setTargetAtTime(0.8, this.ctx.currentTime, 0.08);
    }
  }

  /** Missing character slots are silent, never replaced with unrelated dialogue. */
  private queueVoice(id: string, delay = 0, onEnded?: () => void): boolean {
    if (!this.ctx || !this.voices || !this._volume || !this._voiceVolume) return false;
    const buffer = this.buffers.get(id); if (!buffer) return false;
    const source = this.ctx.createBufferSource(); source.buffer = buffer;
    source.connect(this.voices);
    const start = Math.max(this.ctx.currentTime, this.voiceEnd) + delay;
    this.voiceEnd = start + buffer.duration;
    this.voiceSources.add(source);
    source.onended = () => { this.voiceSources.delete(source); source.disconnect(); onEnded?.(); };
    if (this.effects) {
      this.effects.gain.setTargetAtTime(0.27, start, 0.04);
      this.effects.gain.setTargetAtTime(0.8, this.voiceEnd, 0.16);
    }
    source.start(start);
    return true;
  }

  playEvent(event: SoundEvent, cardType?: CardSound) {
    if (event.type === 'power') {
      const voice = POWER_VOICES[event.cardId ?? ''];
      if (voice && this.ctx && this.ctx.currentTime >= this.voiceEnd) this.queueVoice(voice);
      return;
    }
    if (event.type === 'attack') {
      this.play(cardType === 'energy' || /energy|beam|cannon/i.test(event.message ?? '') ? 'energy-attack' : 'physical-attack');
    } else if (event.type === 'block') this.play(cardType === 'physical' ? 'physical-block' : 'energy-block');
    else if (event.type === 'play') this.play(cardType === 'drill' ? 'drill' : cardType === 'dragon-ball' ? 'capture' : 'card');
    else this.play(event.type);
  }

  /** Legacy event-name API, also used for menu and preview sounds. */
  play(kind: string) {
    if (!this.ctx || this.ctx.state !== 'running' || !this._volume) return;
    const now = this.ctx.currentTime;
    const minimumGap = /draw|damage|power/.test(kind) ? 0.12 : 0.055;
    if (now - (this.lastPlayed.get(kind) ?? -100) < minimumGap) return;
    this.lastPlayed.set(kind, now);
    if (kind === 'decision') {
      // A short scouter-style notification through the existing effects/master mix.
      // The running/mute guard above drops unavailable cues instead of queuing them.
      this.tone(1320, 1320, 0.095, 0.065, 'sine');
      this.tone(1760, 1760, 0.115, 0.065, 'sine', 0.125);
    } else if (kind === 'energy-attack' || kind === 'attack') {
      if (!this.clip('energy-beam', 0.9)) this.energy();
      this.tone(100, 36, 0.26, 0.13, 'sine');
    } else if (kind === 'physical-attack') {
      if (!this.clip('physical-swing', 0.8)) this.noise(0.16, 0.25, 800, 5000);
    } else if (/damage|hit/.test(kind)) {
      if (!this.clip('physical-hit', 0.8)) this.noise(0.2, 0.4, 1800, 170);
      this.tone(125, 35, 0.23, 0.3, 'sine');
    } else if (/block|combat-cancelled/.test(kind)) {
      if (!this.clip(kind === 'physical-block' ? 'physical-swing' : 'energy-block', 0.75)) this.noise(0.25, 0.2, 5000, 800);
      this.tone(880, 320, 0.14, 0.055, 'triangle');
    } else if (/level|power-up|anger|charge/.test(kind)) {
      if (!this.clip('energy-charge', /level/.test(kind) ? 0.85 : 0.48)) this.energy();
      if (/level/.test(kind)) [220, 330, 440].forEach((f, i) => this.tone(f, f * 2, 0.48, 0.055, 'triangle', i * 0.085));
    } else if (kind === 'scouter') {
      if (!this.clip('scouter', 0.6)) [1200, 1600, 2000].forEach((f, i) => this.tone(f, f, 0.045, 0.045, 'sine', i * 0.08));
    } else if (kind === 'victory') {
      this.noise(0.55, 0.12, 3000, 600);
      [261.63, 329.63, 392, 523.25, 659.25].forEach((f, i) => this.tone(f, f, 1.1, 0.08, 'triangle', i * 0.12));
    } else if (/capture|dragon/.test(kind)) {
      [523, 659, 784, 1046].forEach((f, i) => this.tone(f, f * 1.005, 0.44, 0.065, 'sine', i * 0.065));
    } else if (/draw|play|card|discard/.test(kind)) {
      this.noise(0.095, 0.13, 3500, 1500); this.tone(410, 220, 0.075, 0.065, 'triangle');
    } else if (/rejuvenate|drill/.test(kind)) {
      [440, 660].forEach((f, i) => this.tone(f, f, 0.22, 0.055, 'sine', i * 0.075));
    } else if (/select|hover|menu/.test(kind)) this.tone(940, 680, 0.055, 0.045, 'sine');
  }

  private clip(id: string, level: number): boolean {
    const buffer = this.buffers.get(id); if (!buffer || !this.ctx || !this.effects) return false;
    if (this.effectSources.length >= 10) { const oldest = this.effectSources.shift(); try { oldest?.stop(); } catch {} }
    const source = this.ctx.createBufferSource(), gain = this.ctx.createGain();
    source.buffer = buffer; gain.gain.value = level;
    source.connect(gain); gain.connect(this.effects); this.effectSources.push(source);
    source.onended = () => { this.effectSources = this.effectSources.filter(s => s !== source); source.disconnect(); gain.disconnect(); };
    source.start(); return true;
  }

  private tone(from: number, to: number, duration: number, level: number, type: OscillatorType, delay = 0) {
    if (!this.ctx || !this.effects) return;
    const t = this.ctx.currentTime + delay, oscillator = this.ctx.createOscillator(), gain = this.ctx.createGain();
    oscillator.type = type; oscillator.frequency.setValueAtTime(from, t);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, to), t + duration);
    gain.gain.setValueAtTime(0.0001, t); gain.gain.exponentialRampToValueAtTime(level, t + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    oscillator.connect(gain); gain.connect(this.effects); oscillator.start(t); oscillator.stop(t + duration + 0.01);
    oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
  }

  private noise(duration: number, level: number, from: number, to: number) {
    if (!this.ctx || !this.effects) return;
    const t = this.ctx.currentTime, count = Math.ceil(this.ctx.sampleRate * duration);
    const buffer = this.ctx.createBuffer(1, count, this.ctx.sampleRate), data = buffer.getChannelData(0);
    for (let i = 0; i < count; i++) data[i] = Math.random() * 2 - 1;
    const source = this.ctx.createBufferSource(), filter = this.ctx.createBiquadFilter(), gain = this.ctx.createGain();
    source.buffer = buffer; filter.type = 'bandpass'; filter.Q.value = 0.7;
    filter.frequency.setValueAtTime(from, t); filter.frequency.exponentialRampToValueAtTime(to, t + duration);
    gain.gain.setValueAtTime(level, t); gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    source.connect(filter); filter.connect(gain); gain.connect(this.effects); source.start();
    source.onended = () => { source.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  private energy() {
    this.noise(0.55, 0.17, 300, 3500);
    this.tone(110, 760, 0.45, 0.05, 'sawtooth');
    this.tone(113, 770, 0.45, 0.045, 'sawtooth');
  }
}
